import React, { createContext, useState, useContext, useEffect, useMemo } from "react";
import axios from "axios";
import { getToken } from "../utils/tokenUtils";

// Create the HiringRequestsContext
const EmergencyContactsContext = createContext();

// Custom hook to use the context
export const useEmergencyContacts = () => useContext(EmergencyContactsContext);

// Provider component
export const EmergencyContactsProvider = ({ children }) => {
  const [emergencyContacts, setEmergencyContacts] = useState([]);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    perPage: 10,
  });
  const [isLoading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const API_URL = "https://hrmspayroll.prepstripe.com/api/v1/";
  const token = getToken();

  const axiosInstance = useMemo(
    () =>
      axios.create({
        baseURL: API_URL,
        headers: {
          Authorization: `Bearer ${getToken()}`,
        },
      }),
    [API_URL, token]
  );

  // Fetch all hiring requests
  const fetchEmergencyContacts = async (page = 1) => {
    try {
      setLoading(true);
      setError(null); // Reset error state
      const response = await axiosInstance.get(`emergency-contacts?page=${page}`);
      setEmergencyContacts(response.data.data.data);
      setPagination({
        currentPage: response.data?.data?.current_page || 1,
        totalPages: response.data?.data?.last_page || 1,
        totalItems: response.data?.data?.total || 0,
        perPage: response.data?.data?.per_page || 10,
      });
      setLoading(false);
    } catch (err) {
      console.error("Error fetching emergency contacts:", err);
      setError(err.response?.data?.message || "Failed to fetch emegency contacts.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEmergencyContacts();
  }, [axiosInstance]);

  const addEmergencyContact = async (newContact) => {
    try {
      const response = await axiosInstance.post("emergency-contacts", newContact);
      // setEmergencyContacts((prevRequests) => [...prevRequests, response.data.data]);
      setEmergencyContacts((prevRequests) => [response.data?.data, ...prevRequests]);
    } catch (err) {
      console.error("Error adding emergency contact:", err);
      setError(err.response?.data?.message || "Failed to add emergency contact.");
    }
  };

  const updateEmergencyContact= async (updatedContact) => {
    try {
      const response = await axiosInstance.put(`emergency-contacts/${updatedContact.employee_id}`, updatedContact);
      const updatedData = response.data.data;
      setEmergencyContacts((prevContacts) =>
        prevContacts.map((contact) =>
          contact.employee_id === updatedData.employee_id ? { ...contact, ...updatedData } : contact
        )
      );
    } catch (err) {
      console.error("Error updating emergency contact:", err);
      setError(err.response?.data?.message || "Failed to update emergency contact.");
    }
  };

  const deleteEmergencyContact = async (id) => {
    try {
      await axiosInstance.delete(`emergency-contacts/${id}`);
      setEmergencyContacts((prevContacts) => prevContacts.filter((contact) => contact.contact_id !== id));
    } catch (err) {
      console.error("Error deleting emergency contact:", err);
      setError(err.response?.data?.message || "Failed to delete emergency contact.");
    }
  };

  const value = useMemo(
    () => ({
      emergencyContacts,
      isLoading,
      error,
      pagination,
      fetchEmergencyContacts,
      addEmergencyContact,
      updateEmergencyContact,
      deleteEmergencyContact,
    }),
    [emergencyContacts, isLoading, error]
  );

  return <EmergencyContactsContext.Provider value={value}>{children}</EmergencyContactsContext.Provider>;
};
